import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import Modal from './Modal'
import { inputClass } from './Field'
import { AppState, ActualEntry, BudgetItem, ExpenseRule } from '../types'
import { budgetVariance } from '../lib/cashflowEngine'
import { formatMoney, formatSigned, formatDateShort } from '../lib/format'
import { fromISODate, toISODate } from '../lib/dateRules'
import { makeId } from '../lib/id'

/** A budget is spent against; a fixed outgoing is paid. The screen is the same, the words aren't. */
const WORDS = {
  budget: { title: 'budget vs actual', planned: 'Budgeted', plannedSoFar: 'Budgeted so far', spent: 'Actually spent', over: 'Over budget', under: 'Under budget', none: 'budget' },
  expense: { title: 'planned vs actual', planned: 'Planned', plannedSoFar: 'Planned so far', spent: 'Actually paid', over: 'Over plan', under: 'Under plan', none: 'outgoing' },
} as const

interface Props {
  /** Income actuals come from statement imports only; there is no screen to edit them by hand yet. */
  budget: BudgetItem | ExpenseRule
  state: AppState
  year: number
  currencySymbol: string
  onSetActual: (entry: ActualEntry) => void
  onClearActual: (sourceId: string, date: string) => void
  onClose: () => void
}

export default function BudgetActualsModal({
  budget,
  state,
  year: initialYear,
  currencySymbol,
  onSetActual,
  onClearActual,
  onClose,
}: Props) {
  const [year, setYear] = useState(initialYear)
  const words = WORDS[budget.kind]

  const rows = useMemo(
    () => budgetVariance(state, budget, new Date(year, 0, 1), new Date(year, 11, 31)),
    [state, budget, year],
  )

  // Bring today's occurrence (or the latest one before it) into view when the
  // modal opens or the year changes — but not on every edit.
  const scrollRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLTableSectionElement>(null)
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const hasRows = rows.length > 0
  useLayoutEffect(() => {
    const container = scrollRef.current
    if (!container || !hasRows) return
    const todayIso = toISODate(new Date())
    const target = [...rows].reverse().find((r) => r.date <= todayIso) ?? rows[0]
    const row = rowRefs.current[target.date]
    if (!row) return
    container.scrollTop = row.offsetTop - (headRef.current?.offsetHeight ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, hasRows])

  // Drafts keep typing responsive; every edit is still committed immediately
  // so the calendar behind the modal updates as you go.
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  function valueFor(date: string, actual?: number) {
    return drafts[date] ?? (actual !== undefined ? String(actual) : '')
  }

  function handleChange(date: string, raw: string) {
    setDrafts((d) => ({ ...d, [date]: raw }))
    const trimmed = raw.trim()
    if (trimmed === '') {
      onClearActual(budget.id, date)
      return
    }
    const parsed = parseFloat(trimmed)
    if (Number.isNaN(parsed)) return
    const existing = state.actuals.find((a) => a.sourceId === budget.id && a.date === date)
    onSetActual({
      id: existing?.id ?? makeId(),
      sourceId: budget.id,
      date,
      amount: Math.abs(parsed),
      note: existing?.note,
    })
  }

  const recorded = rows.filter((r) => r.actual !== undefined)
  const totalBudgeted = recorded.reduce((sum, r) => sum + r.budgeted, 0)
  const totalActual = recorded.reduce((sum, r) => sum + (r.actual ?? 0), 0)
  const net = totalBudgeted - totalActual

  return (
    <Modal title={`${budget.name} — ${words.title}`} onClose={onClose} width="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-ink/70 max-w-sm">
          Record what each occurrence actually cost. Actuals replace the forecast in your projected
          balance, and the running column shows whether you're up or down overall.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setYear((y) => y - 1)} className="px-2 py-1 text-ink/75 hover:text-ink">
            ‹
          </button>
          <span className="font-display text-base text-ink w-12 text-center">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} className="px-2 py-1 text-ink/75 hover:text-ink">
            ›
          </button>
        </div>
      </div>

      {recorded.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
          <div className="border border-line rounded p-2">
            <div className="text-[11px] text-ink/70">{words.plannedSoFar}</div>
            <div className="font-mono font-tabular text-ink">{formatMoney(totalBudgeted, currencySymbol)}</div>
          </div>
          <div className="border border-line rounded p-2">
            <div className="text-[11px] text-ink/70">{words.spent}</div>
            <div className="font-mono font-tabular text-ink">{formatMoney(totalActual, currencySymbol)}</div>
          </div>
          <div className="border border-line rounded p-2">
            <div className="text-[11px] text-ink/70">{net < 0 ? words.over : words.under}</div>
            <div className={`font-mono font-tabular ${net < 0 ? 'text-rag-bad' : 'text-rag-good'}`}>
              {formatSigned(net, currencySymbol)}
            </div>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-ink/70">No occurrences of this {words.none} in {year}.</p>
      ) : (
        <div ref={scrollRef} className="max-h-[45vh] overflow-y-auto border-y border-line">
          <table className="w-full text-sm">
            <thead ref={headRef} className="sticky top-0 bg-white">
              <tr className="text-[11px] font-sans uppercase tracking-wide text-ink/70 text-left">
                <th className="py-2 font-normal">Date</th>
                <th className="py-2 font-normal text-right">{words.planned}</th>
                <th className="py-2 font-normal text-right w-28">Actual</th>
                <th className="py-2 font-normal text-right">Variance</th>
                <th className="py-2 font-normal text-right">Running</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr
                  key={r.date}
                  ref={(el) => {
                    rowRefs.current[r.date] = el
                  }}
                >
                  <td className="py-1.5 text-ink">{formatDateShort(fromISODate(r.date))}</td>
                  <td className="py-1.5 text-right font-mono font-tabular text-ink/75">
                    {formatMoney(r.budgeted, currencySymbol)}
                  </td>
                  <td className="py-1.5 pl-2">
                    <input
                      className={`${inputClass} text-right py-1`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="—"
                      value={valueFor(r.date, r.actual)}
                      onChange={(e) => handleChange(r.date, e.target.value)}
                    />
                  </td>
                  <td
                    className={`py-1.5 text-right font-mono font-tabular ${
                      r.variance === undefined ? 'text-ink/70' : r.variance < 0 ? 'text-rag-bad' : 'text-rag-good'
                    }`}
                  >
                    {r.variance === undefined ? '—' : formatSigned(r.variance, currencySymbol)}
                  </td>
                  <td
                    className={`py-1.5 text-right font-mono font-tabular ${
                      r.runningVariance < 0 ? 'text-rag-bad' : 'text-rag-good'
                    }`}
                  >
                    {formatSigned(r.runningVariance, currencySymbol)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
          Done
        </button>
      </div>
    </Modal>
  )
}
